import { ActionReducerMapBuilder } from '@reduxjs/toolkit'
import { fromPairs } from 'lodash'
import flow from 'lodash/fp/flow'
import set from 'lodash/fp/set'
import update from 'lodash/fp/update'
import get from 'lodash/get'
import has from 'lodash/has'
import isEmpty from 'lodash/isEmpty'
import mapValues from 'lodash/mapValues'
import { normalize } from 'normalizr'

import { changeCurrencyCode } from 'actions/ui/app'
import {
    fetchDashboardSuccess,
    updateWidgetPagination,
    updateWidgetSorter,
    updateWidgetTableColumns,
} from 'actions/ui/shared/dashboard'
import { DATES, isFilterValueTruthy } from 'const/filters'
import { FILTER_SETTINGS, FILTERS } from 'const/reducerKeys'
import { derivesRangeLag } from 'features/Dashboards/derivedLagPages'
import { serializeDatesFilter } from 'helpers/filters'
import { getCurrentPage } from 'routing/pagePaths'
import { tabListSchema } from 'schemas'
import { FilterKey } from 'types'

import {
    AVAILABLE_TAB_FILTERS,
    defaultDashboardTabState,
    defaultDatesFilter,
    defaultPaginationAndSorter,
    defaultWidgetLayout,
    defaultWidgetState,
} from '../defaults'

import { SharedState } from './state'

// Cases added to the shared/index createReducer; state is parent UI state
export const addDashboardCases = (
    builder: ActionReducerMapBuilder<SharedState>
): void => {
    builder
        .addCase(fetchDashboardSuccess, (state, action) => {
            const { pageName, data: dashboard } = action.payload
            const {
                entities: { tabs, widgets = {} },
            } = normalize(get(dashboard, ['config', 'tabs'], []), tabListSchema)

            // If we have dashboard filters stored in config, serialize them;
            // Use the defaults from page reducer otherwise
            let filters = get(dashboard, ['config', FILTERS])
            if (filters) {
                filters = {
                    ...filters,
                    [DATES]: serializeDatesFilter(filters[DATES]),
                }
            } else {
                filters = get(state, [pageName, FILTERS])
            }

            return flow(
                set([pageName, 'dashboard'], dashboard),
                set(
                    [pageName, 'currencyCode'],
                    get(dashboard, ['config', 'currency_code'], null)
                ),
                set(
                    [pageName, 'tabs'],
                    mapValues(tabs, (tab) => {
                        const processedTab = {
                            ...defaultDashboardTabState,
                            ...tab,
                        }

                        if (
                            pageName === 'dashboardPage' ||
                            pageName === 'homePage'
                        ) {
                            let dateRangeFilter: string | string[] =
                                defaultDatesFilter
                            if (!isEmpty(tab?.localFilters?.[DATES])) {
                                dateRangeFilter = serializeDatesFilter(
                                    tab.localFilters[DATES]
                                )
                            } else if (!isEmpty((filters || {})[DATES])) {
                                dateRangeFilter = filters[DATES]
                            }

                            const userAddedFilters =
                                AVAILABLE_TAB_FILTERS.filter((filterKey) => {
                                    if (filterKey === DATES) {
                                        return (
                                            processedTab.localFilters?.[
                                                DATES
                                            ] !== defaultDatesFilter
                                        )
                                    }
                                    return isFilterValueTruthy(
                                        processedTab.localFilters?.[filterKey]
                                    )
                                })

                            // TODO: right now in the home dashboard we only show the filters defined in the dashboard.
                            //  should we allow the user to play with all the filters?
                            let availableFilters: FilterKey[] = []

                            if (pageName === 'dashboardPage') {
                                availableFilters = AVAILABLE_TAB_FILTERS
                            } else if (pageName === 'homePage') {
                                // home page need to have the addedFilters in the state so they can be used.
                                availableFilters = userAddedFilters
                            }

                            processedTab.localFilters = {
                                ...fromPairs(
                                    availableFilters.map((filter) => [
                                        filter,
                                        undefined,
                                    ])
                                ),
                                ...processedTab.localFilters,
                                [DATES]: dateRangeFilter,
                                // A saved lag is ignored: the widgets work it out
                                // from their sources (features/Dashboards/rangeLag).
                                rangeLag: undefined,
                            }

                            processedTab[FILTER_SETTINGS] = {
                                anchored: [],
                                order: availableFilters,
                                userAdded: userAddedFilters,
                                // no clear button in the home page
                                touched:
                                    userAddedFilters.length > 0 &&
                                    pageName === 'dashboardPage',
                                displayState: {},
                            }
                        }

                        return processedTab
                    })
                ),
                set(
                    [pageName, 'widgets'],
                    // set default ui state on each widget
                    mapValues(widgets, (widget) => ({
                        ...widget,
                        data_source:
                            // DOW-10796 backwards compatibility:
                            // Convert legacy Stream widgets to the new unified data source
                            // As users save their dashboards, widgets will be in the correct state automatically
                            widget.data_source === 'ams_stream_traffic_facts' ||
                            widget.data_source === 'ams_stream_conversion_facts'
                                ? 'ams_stream_facts'
                                : widget.data_source,
                        filters: {
                            ...widget.filters,
                            ...(!isEmpty(widget?.filters?.[DATES]) && {
                                [DATES]: serializeDatesFilter(
                                    widget.filters[DATES]
                                ),
                            }),
                            // A saved dashboard's lag is ignored: the widgets work it
                            // out from their sources (features/Dashboards/rangeLag).
                            rangeLag: derivesRangeLag(pageName)
                                ? undefined
                                : isEmpty(widget?.filters?.rangeLag)
                                  ? widget?.filters?.rangeLag
                                  : parseInt(
                                        widget.filters.rangeLag[0]?.value ?? 0,
                                        10
                                    ),
                        },
                        ...defaultWidgetState,
                        ...defaultPaginationAndSorter(widget),
                        ...defaultWidgetLayout(widget),
                    }))
                ),
                set([pageName, FILTERS], filters),
                set([pageName, 'isDirty'], false)
            )(state)
        })

        // update pagination
        .addCase(updateWidgetPagination, (state, action) => {
            const {
                pageName,
                data: { widgetId, pagination },
            } = action.payload

            return update(
                [pageName, 'widgets', widgetId, 'pagination'],
                (prevPagination) => ({ ...prevPagination, ...pagination })
            )(state)
        })

        // update sorter
        .addCase(updateWidgetSorter, (state, action) => {
            const {
                pageName,
                data: { widgetId, sorter },
            } = action.payload

            return set([pageName, 'widgets', widgetId, 'sorter'], sorter)(state)
        })

        // update table settings
        .addCase(updateWidgetTableColumns, (state, action) => {
            const {
                pageName,
                data: { widgetId, metrics, group_by },
            } = action.payload

            return flow(
                set([pageName, 'widgets', widgetId, 'metrics'], metrics),
                set([pageName, 'widgets', widgetId, 'group_by'], group_by)
            )(state)
        })

        .addCase(changeCurrencyCode, (state) => {
            const { page } = getCurrentPage()
            if (!!page && has(state, [page, 'tabs'])) {
                return update([page, 'tabs'], (tabs) =>
                    mapValues(tabs, (tab) => ({
                        ...tab,
                        loaded: false,
                    }))
                )(state)
            }
            return state
        })
}
